import logo from '../content/SmartMesh500x500.png';
import '../styles/Aside.css';
import Navigation from './Navigation';
import CompanyDetails from './CompanyDetails';
import { useState, useRef, useEffect } from 'react';

const Aside: React.FC = () => {
    const [showPopover, setShowPopover] = useState(false);
    const [collapsed, setCollapsed] = useState(true); // collapsed by default on mobile
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Always read theme from body class for correct sync
    const getTheme = () => {
      // Prefer localStorage, fallback to body class
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') return stored;
      return document.body.classList.contains('dark') ? 'dark' : 'light';
    };
    const [theme, setTheme] = useState<'light' | 'dark'>(getTheme());

    // On mount, apply theme from localStorage
    useEffect(() => {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') {
        document.body.classList.remove('light', 'dark');
        document.body.classList.add(stored);
        const app = document.querySelector('.App');
        if (app) {
          app.classList.remove('light', 'dark');
          app.classList.add(stored);
        }
        setTheme(stored);
      }
    }, []);

    // Theme toggle handler (sync with App)
    const toggleTheme = () => {
      const newTheme = getTheme() === 'light' ? 'dark' : 'light';
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(newTheme);
      setTheme(newTheme);
      localStorage.setItem('theme', newTheme); // persist theme
      // Also update App container class for CSS variables
      const app = document.querySelector('.App');
      if (app) {
        app.classList.remove('light', 'dark');
        app.classList.add(newTheme);
      }
    };

    // Listen for theme changes
    useEffect(() => {
      const observer = new MutationObserver(() => {
        setTheme(getTheme());
      });
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      return () => observer.disconnect();
    }, []);

    // Listen for window resize to auto-expand on desktop
    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth > 768) setCollapsed(false);
        else setCollapsed(true);
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Set body class for main-body adaptation
    useEffect(() => {
      if (collapsed) document.body.classList.add('aside-collapsed');
      else document.body.classList.remove('aside-collapsed');
    }, [collapsed]);

    // Overlay for mobile
    const showOverlay = !collapsed && window.innerWidth <= 768;

    return (
      <>
        <button
          className="aside-toggle-btn"
          aria-label={collapsed ? 'Open navigation' : 'Close navigation'}
          onClick={() => setCollapsed(c => !c)}
        >
          {collapsed ? (
            <span style={{fontSize: 22}}>&#9776;</span>
          ) : (
            <span style={{fontSize: 22}}>&#10005;</span>
          )}
        </button>
        <div className={`aside-root d-flex flex-column flex-shrink-0 p-3${collapsed ? ' collapsed' : ''}`}
          style={window.innerWidth > 768 ? {height: '100vh'} : {}}
        >
          <div className="aside-logo-row">
            <a href="/" className="d-flex align-items-center text-decoration-none" style={{color: 'inherit'}}>
              <img src={logo} alt="logo" className="logo"/>
              <span className="fs-4">SmartMesh</span>
            </a>
          </div>
          <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
            <Navigation/>
          </div>
          {/* Only show buttons if aside is open on mobile, or always on desktop */}
          {(!collapsed || window.innerWidth > 768) && (
            <div style={{position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 8}}>
              <button
                className="contact-popover-btn btn btn-outline-secondary"
                style={{borderRadius: '2rem', height: 44, display: 'flex', alignItems: 'center', gap: 8, padding: '0 1.25rem', fontWeight: 500, marginBottom: 4}}
                onClick={toggleTheme}
                aria-label="Toggle light/dark mode"
              >
                {theme === 'light' ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16"><path d="M6 0a.5.5 0 0 1 .5.5V2a.5.5 0 0 1-1 0V.5A.5.5 0 0 1 6 0zm3.364 1.05a.5.5 0 0 1 .707 0l1.06 1.06a.5.5 0 1 1-.707.708l-1.06-1.06a.5.5 0 0 1 0-.708zM16 6a.5.5 0 0 1-.5.5H14a.5.5 0 0 1 0-1h1.5A.5.5 0 0 1 16 6zM2 6.5A.5.5 0 0 1 2 6H.5a.5.5 0 0 1 0-1H2a.5.5 0 0 1 0 1zm11.364 7.364a.5.5 0 0 1-.707 0l-1.06-1.06a.5.5 0 1 1 .707-.708l1.06 1.06a.5.5 0 0 1 0 .708zM6 14a.5.5 0 0 1-.5-.5V14a.5.5 0 0 1 1 0v1.5a.5.5 0 0 1-1 0V14zm-4.95-2.364a.5.5 0 0 1 0-.707l1.06-1.06a.5.5 0 1 1 .707.707l-1.06 1.06a.5.5 0 0 1-.707 0zM3.636 3.636a.5.5 0 0 1 0-.707l1.06-1.06a.5.5 0 1 1 .707.707l-1.06 1.06a.5.5 0 0 1-.707 0zM8 4a4 4 0 1 0 0 8A4 4 0 0 0 8 4z"/></svg>
                    Dark Mode
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16"><path d="M12.454 10.708A5.5 5.5 0 0 1 5.292 3.546a6 6 0 1 0 7.162 7.162z"/></svg>
                    Light Mode
                  </>
                )}
              </button>
              <button
                ref={buttonRef}
                className="contact-popover-btn btn btn-outline-secondary"
                style={{borderRadius: '2rem', height: 44, display: 'flex', alignItems: 'center', gap: 8, padding: '0 1.25rem', fontWeight: 500}}
                onClick={() => setShowPopover(v => !v)}
                aria-label="Show contact info"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                </svg>
                Contact Info
              </button>
              {showPopover && (
                <div
                  className={`popover bs-popover-up show ${theme}`}
                  style={{
                    position: 'absolute',
                    bottom: 54,
                    left: 0,
                    transform: 'none',
                    zIndex: 2000,
                    minWidth: 220,
                    backgroundColor: 'var(--background)',
                    color: 'var(--foreground)',
                    border: '1px solid var(--foreground)'
                  }}
                  onMouseLeave={() => setShowPopover(false)}
                >
                  <div className="popover-arrow" style={{left: 32}}></div>
                  <div className="popover-body">
                    <CompanyDetails theme={theme}/>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {showOverlay && <div className="aside-overlay" onClick={() => setCollapsed(true)}></div>}
      </>
    );
}

export default Aside;
